import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getCurrency, getLanguage, L10nDate, L10nNumber } from '@travel/i18n';
import {
  CreditCardOutline,
  None,
  NonRefundable,
  PaymentMethod,
  Restaurant,
} from '@travel/icons/service';
import { Checkmark, RakutenPoints } from '@travel/icons/ui';
import Translate from '@travel/traveler-core/components/Translate';
import {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
  useDeviceType,
  useDialogHandler,
} from '@travel/traveler-core/hooks';
import type {
  Facility,
  PlanPaymentRestrictionFeature,
  Price,
} from '@travel/traveler-core/types/providerRatePlan';
import { IconText, IconTextList, ImagePlaceHolder, Popup } from '@travel/ui';
import type { GalleryMedia } from '@travel/ui/components/Gallery';
import { cx, isNotEmptyArray } from '@travel/utils';

import Media from 'components/Media';
import MembersOnlyTag from 'components/MembersOnlyTag';
import PhotoGalleryDetail from 'components/PhotoGalleryDetail';
import type {
  InternalSalesPromotionFormQuery,
  SalesPromotionFormQuery,
} from 'components/SalesPromotionInput';
import { getFilteredRoomPlanFeatures } from 'pages/ProviderInformationPage/helpers';
import RoomUrgencyMessage from 'pages/ProviderListPage/components/ProviderBox/RoomUrgencyMessage';

import { getIsDirectContractType } from 'store/providerInformation/selectors';

import { GALLERY_QUERY, GALLERY_QUERY_VALUE } from 'constants/gallery';
import { PAY_AT_HOTEL } from 'constants/planFeatures';
import useQueryToggle from 'hooks/useQueryToggle';
import type { RoomFormQuery } from 'ProviderList-Types';
import type { CancellationPenalties } from 'ProviderRatePlan-Types';

import CancellationPolicy from '../CancellationPolicy';
import SeeMoreLink from '../SeeMoreLink';
import PriceInfo from './PriceInfo';

import styles from './roomPlanItemCard.module.scss';

type Props = {
  itemType: 'plan' | 'room';
  availabilityCount?: number;
  cancellationPenalties?: Array<CancellationPenalties>;
  hasMeal?: boolean;
  facilities?: Array<Facility>;
  media?: Array<GalleryMedia>;
  paymentRestrictionFeature?: PlanPaymentRestrictionFeature;
  itemTitle?: string;
  price: Price;
  points?: {
    amount: number;
    currency: string;
  };
  searchParam: RoomFormQuery & SalesPromotionFormQuery & InternalSalesPromotionFormQuery;
  couponBadge?: false | JSX.Element;
  roomBasicInfoSummary?: JSX.Element;
  onDisplayDetailDialog: () => void;
  onReserve: () => void;
  onSelectDate?: (startDate?: string, endDate?: string) => void;
  itemRatePlanId: string;
  paymentMethods: Array<string>;
  isNonRefundable?: boolean;
  cancellationRemarks?: string;
  isRakutenMembership?: boolean;
};

function RoomPlanItemCard(props: Props) {
  const {
    itemType,
    availabilityCount,
    cancellationPenalties,
    hasMeal,
    facilities,
    media,
    itemTitle,
    price,
    onDisplayDetailDialog,
    searchParam,
    couponBadge,
    roomBasicInfoSummary,
    itemRatePlanId,
    points,
    paymentMethods,
    isNonRefundable,
    cancellationRemarks,
    isRakutenMembership,
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const { isOpen: isOpenGallery, onClose: onCloseGallery, onOpen: onOpenGallery } = useQueryToggle(
    GALLERY_QUERY,
    `${GALLERY_QUERY_VALUE.PlanRoomCard}-${itemRatePlanId}`,
  );
  const isDirectContractType = useSelector(getIsDirectContractType);
  const isPayAtHotel = paymentMethods.includes(PAY_AT_HOTEL);

  const {
    isOpen: isOpenCancellationPolicyPopup,
    onClose: onCloseCancellationPolicyPopup,
    onOpen: onOpenCancellationPolicyPopup,
  } = useDialogHandler(false);

  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isDesktop = deviceType === DESKTOP_SCREEN;

  const language = useSelector(getLanguage);
  const currency = useSelector(getCurrency);

  const ROOM_AVAILABILITY_CHECK = 10;
  const shouldShowUrgencyMessage =
    searchParam.startDate &&
    searchParam.endDate &&
    availabilityCount &&
    availabilityCount < ROOM_AVAILABILITY_CHECK;
  const shouldShowUrgencyTag = !!(!isMobile && shouldShowUrgencyMessage && availabilityCount);
  const shouldShowRakutenMembership = isRakutenMembership && itemType === 'plan';

  const freeUntil = cancellationPenalties?.find(item => item.type === 'NO_PENALTY')?.deadline;

  const headerImage = isNotEmptyArray(media) ? (
    <Media
      className={styles.zoomMedia}
      media={media[0]}
      onClick={isMobile ? onDisplayDetailDialog : onOpenGallery}
      sizes="15vw"
      wrapperClassName={cx(styles.headerImage, shouldShowUrgencyTag && styles.shadow)}
    />
  ) : (
    <ImagePlaceHolder
      className={cx(styles.headerImage, shouldShowUrgencyTag && styles.shadow)}
      onClick={isMobile ? onDisplayDetailDialog : undefined}
      {...(isMobile && { size: 40 })}
    />
  );

  const features = getFilteredRoomPlanFeatures(facilities, 0, 4);
  const planFeatureList = [
    freeUntil && (
      <div key={'FreeUntil'}>
        <IconText
          onMouseEnter={isDesktop ? onOpenCancellationPolicyPopup : undefined}
          onMouseLeave={isDesktop ? onCloseCancellationPolicyPopup : undefined}
          className={styles.freeUntil}
          textClassName={styles.iconTextText}
          text={
            !isMobile ? (
              <Translate
                id="Cancellation_Policy_Common.Provider_Pages.Free_Cancellation"
                data={{
                  free_cancellation_date: (
                    <L10nDate value={freeUntil} format="MD" isDisplayAsLocalTime />
                  ),
                }}
              />
            ) : (
              <Translate id="Cancellation_Policy_Common.Provider_List.Free_Cancellation" />
            )
          }
          icon={<Checkmark size={16} color="cilantro" />}
          spaceBetween={4}
        />
        {isDesktop && isNotEmptyArray(cancellationPenalties) && (
          <Popup
            className={styles.cancellationPopup}
            isOpen={isOpenCancellationPolicyPopup}
            onClose={onCloseCancellationPolicyPopup}
            openTo="right"
            isShowTriangle={true}
          >
            <CancellationPolicy
              wrapperClassName={styles.cancellationContainer}
              titleClassName={styles.cancellationTitle}
              cancellationPenalties={cancellationPenalties}
              currency={currency}
              language={language}
              price={price}
              cancellationRemarks={cancellationRemarks}
              isDirectContract={isDirectContractType}
              isOpenCancellationPolicyPopup={isOpenCancellationPolicyPopup}
            />
          </Popup>
        )}
      </div>
    ),
    !hasMeal ? (
      <IconText
        key={'IconNoMeal'}
        className={styles.meal}
        textClassName={styles.iconTextText}
        icon={<None size={16} />}
        text={<Translate id="Common_Price_Details.Guests.No_Meal" />}
        spaceBetween={4}
      />
    ) : (
      <IconText
        key={'IconMeal'}
        className={styles.meal}
        textClassName={styles.iconTextText}
        icon={<Restaurant size={16} />}
        text={<Translate id="Provider_Info.Luxury.Meal" />}
        spaceBetween={4}
      />
    ),
    isNotEmptyArray(features) &&
      features.map(feature => (
        <IconText
          key={feature.id}
          spaceBetween={4}
          {...feature}
          className={styles.features}
          textClassName={styles.iconTextText}
        />
      )),
  ].flat();
  const basicInfo =
    itemType === 'plan' ? (
      (freeUntil || (hasMeal !== undefined && !hasMeal) || isNotEmptyArray(features)) && (
        <>
          {!isDirectContractType && planFeatureList[0] && !isMobile && (
            <div className={styles.featuresWrapper}>{planFeatureList[0]}</div>
          )}
          <div className={cx(styles.featuresWrapper, !isDirectContractType && styles?.o2oFeatures)}>
            {planFeatureList.slice(
              isDirectContractType ? 0 : isMobile ? 0 : 1,
              isMobile ? 4 : undefined,
            )}
          </div>
        </>
      )
    ) : (
      <>
        {roomBasicInfoSummary}
        {!isMobile && isNotEmptyArray(features) && (
          <IconTextList
            className={cx(styles.featuresWrapper, styles.roomFeaturesWrapper)}
            iconTextClassName={styles.features}
            items={features}
            spaceBetween={8}
          />
        )}
      </>
    );

  const indirectContractTypePaymentInfo = (
    <div className={cx(styles?.featuresWrapper, styles.o2oFeatures, styles.paymentRestrictions)}>
      {isNotEmptyArray(paymentMethods) && (
        <IconText
          className={cx(styles.meal, styles.greyBackground)}
          textClassName={styles.iconTextText}
          icon={isPayAtHotel ? <PaymentMethod size={16} /> : <CreditCardOutline size={16} />}
          text={
            <Translate
              id={
                isPayAtHotel
                  ? 'Common_Price_Details.Payment.Pay_On_Site'
                  : 'Traveler_Enumeration.Traveler.Payment_Method.Credit_Card'
              }
            />
          }
          spaceBetween={4}
        />
      )}
      {isNonRefundable && (
        <IconText
          className={cx(styles.meal, styles.greyBackground)}
          textClassName={styles.iconTextText}
          icon={<NonRefundable size={16} />}
          text={<Translate id="Common_Price_Details.Out_To_Out.Payment.Non_Refundable" />}
          spaceBetween={4}
        />
      )}
      {!isMobile && Number(points?.amount) > 0 && (
        <span className={styles.pointItem}>
          <RakutenPoints size={16} />
          <Translate
            id="Provider_Info.Room_List.Plan_Info.Points"
            data={{
              points_calculation: <L10nNumber value={points?.amount || 0} />,
              currency: `${points?.currency || ''}`,
            }}
          />
        </span>
      )}
    </div>
  );

  useEffect(() => {
    const handleScroll = () => {
      const url = new URL(window.location.toString());
      const targetItemRatePlan = url.hash.slice(1);

      if (ref.current && itemRatePlanId === targetItemRatePlan) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    const request = requestAnimationFrame(handleScroll);

    return () => cancelAnimationFrame(request);
  }, [itemRatePlanId]);

  return (
    <div data-testid="providerInformation-roomPlanItemCard">
      <div
        className={cx(
          styles.itemWrapper,
          couponBadge && styles.itemWrapperWithCoupon,
          !isDirectContractType && !isMobile && styles.flexRow,
        )}
      >
        {/* PC room title */}
        {itemType === 'room' && isDesktop && (
          <button
            className={styles.nameButton}
            onClick={onDisplayDetailDialog}
            data-testid="roomPlanItemCard-name-button"
          >
            <h4
              className={cx(styles.name, styles.titleTruncate1)}
              data-testid="roomPlanItemCard-name"
            >
              {itemTitle}
            </h4>
          </button>
        )}
        {/* Plan/Room image + info */}
        <div className={cx(styles.itemInfoWrapper, !isDirectContractType && styles.noBorderBottom)}>
          <div className={styles.headerWrapper}>
            {isDirectContractType && headerImage}
            {isDirectContractType && shouldShowUrgencyTag && (
              <RoomUrgencyMessage
                color="red"
                availabilityCount={availabilityCount}
                tagClassName={styles.urgencyTag}
              />
            )}
            <div className={styles.infoWrapper}>
              {(itemType === 'plan' || (itemType === 'room' && !isDesktop)) && (
                <button
                  className={styles.nameButton}
                  onClick={onDisplayDetailDialog}
                  data-testid="roomPlanItemCard-name-button"
                >
                  <h4 className={cx(styles.name, styles.titleTruncate2)}>{itemTitle}</h4>
                </button>
              )}
              {isMobile &&
                shouldShowUrgencyMessage &&
                availabilityCount &&
                isDirectContractType && (
                  <Translate
                    className={styles.urgencyText}
                    id="Provider_Info.Room_List.Plan_Info.Urgency_message"
                    count={availabilityCount}
                    data={{ number_of_rooms_available: availabilityCount }}
                  />
                )}
              {!isMobile && shouldShowRakutenMembership && (
                <MembersOnlyTag
                  text={<Translate id="Provider_Info.Room_List.Members_Only_Tag" />}
                />
              )}
              {!isMobile && basicInfo}
              {!isDirectContractType && !isMobile && indirectContractTypePaymentInfo}
              {!isMobile && (
                <SeeMoreLink
                  className={styles.seeMoreLink}
                  title={
                    <Translate
                      id={`Provider_Info.Tablet_PC.See_${
                        itemType === 'plan' ? 'Plan' : 'Room'
                      }_Details`}
                    />
                  }
                  onClick={onDisplayDetailDialog}
                  data-testid="roomPlanItemCard-seeMoreLink"
                />
              )}
            </div>
          </div>
          {isMobile && shouldShowRakutenMembership && (
            <MembersOnlyTag text={<Translate id="Provider_Info.Room_List.Members_Only_Tag" />} />
          )}
          {isMobile && basicInfo}
          {!isDirectContractType && isMobile && indirectContractTypePaymentInfo}
        </div>
        <PriceInfo {...props} price={price} />
      </div>
      {couponBadge}
      {isNotEmptyArray(media) && (
        <PhotoGalleryDetail
          isOpen={isOpenGallery}
          onClose={onCloseGallery}
          mediaData={media}
          imageIndex={0}
        />
      )}
    </div>
  );
}

export default RoomPlanItemCard;
